/**
 * Discrepancy Behavior Helpers
 * Defines how different discrepancy types should behave when actions are taken
 */

import { Discrepancy, DiscrepancyType } from '@models/discrepancy.model';

export interface DiscrepancyActionResult {
  shouldCallBackend: boolean;
  shouldNavigate: boolean;
  navigateUrl?: string;
}

/**
 * Determines the behavior when a discrepancy action is selected
 * @param discrepancy - The discrepancy being acted upon
 * @param actionCode - The action code selected (optionWolf, optionI, IGNORE, etc.)
 * @param context - Optional context ('process-page' if already on the process page)
 * @returns Object indicating what actions should be taken
 */
export const getDiscrepancyActionBehavior = (
  discrepancy: Discrepancy,
  actionCode: string,
  context?: 'process-page'
): DiscrepancyActionResult => {
  const discrepancyType = discrepancy?.discrepancyData?.discrepancyType;
  const entityId = discrepancy?.discrepancyData?.entityId;

  // PROCESS_IMPROVEMENT with optionWolf/optionP behavior
  // If already on process page, call backend to mark as solved (user has applied changes)
  // If on discrepancies list page, navigate to the process
  if (
    discrepancyType === DiscrepancyType.PROCESS_IMPROVEMENT &&
    (actionCode === discrepancy?.discrepancyData?.optionWolf ||
      actionCode === discrepancy?.discrepancyData?.optionP ||
      actionCode === 'PET_GOTO_UPDATE_ENTITY')
  ) {
    if (context === 'process-page') {
      // Already on process page, call backend to mark improvements as applied
      return {
        shouldCallBackend: true,
        shouldNavigate: false,
      };
    } else {
      // On discrepancies list, navigate to process with flag to show discrepancies panel
      return {
        shouldCallBackend: false,
        shouldNavigate: true,
        navigateUrl: `/processes/${entityId}?showDiscrepancies=true`,
      };
    }
  }

  // IGNORE actions should call backend to update ignoreUntil
  if (actionCode === 'IGNORE') {
    return {
      shouldCallBackend: true,
      shouldNavigate: false,
    };
  }

  // All other actions should call backend
  return {
    shouldCallBackend: true,
    shouldNavigate: false,
  };
};
